import React, { useEffect, useState } from "react";
import {
  Plus,
  Search,
  Edit2,
  Trash2,
  MapPin,
  Phone,
  Mail,
  User,
  Clock,
  Building2,
  ChevronLeft,
  ChevronRight,
  Smartphone,
  Eye,
  EyeOff,
  Star,
  StarOff,
  BadgePercent, // ✅ commission icon
} from "lucide-react";
import axios from "axios";
import ReferenceCenterModal from "./components/ReferenceCenterModal";
import SlotsManagementModal from "./components/SlotsManagement";
import CenterCommissionsModal from "./components/Centercommissionsmodal"; // ✅ NEW
import { useAuth } from "../../context/AuthContext";
import { canAccess } from "../../utils/permissions";
import Header from "../../components/ui/Header";
import Sidebar from "../../components/ui/Sidebar";
import API_BASE_URL from "../../services/api";
import axiosInstance from "../../utils/axiosInstance";

function Centers() {
  const { user: currentUser } = useAuth();

  const [centers, setCenters] = useState([]);
  const [filteredCenters, setFilteredCenters] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedCenter, setSelectedCenter] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(true);
  const [centerCategories, setCenterCategories] = useState([]);

  // Slots management states
  const [slotsModalOpen, setSlotsModalOpen] = useState(false);
  const [selectedCenterForSlots, setSelectedCenterForSlots] = useState(null);
  const [centerSlots, setCenterSlots] = useState([]);
  const [slotsLoading, setSlotsLoading] = useState(false);

  // ✅ Commissions modal states
  const [commissionsModalOpen, setCommissionsModalOpen] = useState(false);
  const [selectedCenterForCommissions, setSelectedCenterForCommissions] = useState(null);

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalPages, setTotalPages] = useState(1);

  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  const handleMenuToggle = () => setSidebarOpen((p) => !p);

  const calculatePagination = (data) => {
    const pages = Math.max(1, Math.ceil(data.length / itemsPerPage));
    setTotalPages(pages);
    if (currentPage > pages) setCurrentPage(1);
  };

  const loadCenters = async () => {
    try {
      setLoading(true);
      const res = await axiosInstance.get(`/api/centers`);
      const centersData = res.data?.data || res.data || [];
      const normalized = Array.isArray(centersData)
        ? centersData.map((c) => ({
            ...c,
            showApp: Boolean(c.showApp),
            recommended: Boolean(c.recommended),
          }))
        : [];
      setCenters(normalized);
      setFilteredCenters(normalized);
      calculatePagination(normalized);
    } catch (err) {
      console.error("Fetch centers failed:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCenters();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getCurrentPageItems = () => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return filteredCenters.slice(startIndex, startIndex + itemsPerPage);
  };

  useEffect(() => {
    let result = [...centers];
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      result = result.filter((center) => {
        const cityName = typeof center.city === "string" ? center.city.toLowerCase() : (center.city?.name || "").toLowerCase();
        return (
          (center.name || "").toLowerCase().includes(term) ||
          (center.contactName || "").toLowerCase().includes(term) ||
          (center.email || "").toLowerCase().includes(term) ||
          (center.address || "").toLowerCase().includes(term) ||
          cityName.includes(term) ||
          (center.isSelf ? "own" : "partner").includes(term)
        );
      });
    }
    setFilteredCenters(result);
    calculatePagination(result);
    setCurrentPage(1);
  }, [centers, searchTerm, itemsPerPage]);

  const toggleShowApp = async (center) => {
    const next = !Boolean(center.showApp);
    setCenters((prev) => prev.map((c) => (c.id === center.id ? { ...c, showApp: next } : c)));
    try {
      const res = await axiosInstance.put(`/api/centers/${center.id}`, { showApp: next });
      const updated = res.data?.center || res.data;
      setCenters((prev) => prev.map((c) => c.id === center.id ? { ...c, showApp: Boolean(updated?.showApp ?? next) } : c));
    } catch (e) {
      console.error("Failed to toggle showApp:", e);
      setCenters((prev) => prev.map((c) => (c.id === center.id ? { ...c, showApp: !next } : c)));
      alert("Failed to update Show in App");
    }
  };

  const toggleRecommended = async (center) => {
    const next = !Boolean(center.recommended);
    setCenters((prev) => prev.map((c) => (c.id === center.id ? { ...c, recommended: next } : c)));
    try {
      const res = await axiosInstance.put(`/api/centers/${center.id}`, { recommended: next });
      const updated = res.data?.center || res.data;
      setCenters((prev) => prev.map((c) => c.id === center.id ? { ...c, recommended: Boolean(updated?.recommended ?? next) } : c));
    } catch (e) {
      console.error("Failed to toggle recommended:", e);
      setCenters((prev) => prev.map((c) => (c.id === center.id ? { ...c, recommended: !next } : c)));
      alert("Failed to update Recommended");
    }
  };

  const handleOpenSlotsModal = async (center) => {
    setSelectedCenterForSlots(center);
    setSlotsLoading(true);
    try {
      const catRes = await axiosInstance.get(`${API_BASE_URL}/api/centers/${center.id}/categories`);
      setCenterCategories(catRes.data?.categories || []);
      const slotRes = await axiosInstance.get(`${API_BASE_URL}/api/centers/${center.id}/slots`);
      setCenterSlots(slotRes.data?.slots || []);
      setSlotsModalOpen(true);
    } catch (error) {
      console.error("Failed to open slots modal:", error);
      alert("Failed to load slot data");
      setCenterSlots([]);
      setCenterCategories([]);
    } finally {
      setSlotsLoading(false);
    }
  };

  const handleSlotsUpdated = async () => {
    if (selectedCenterForSlots) {
      setSlotsLoading(true);
      try {
        const res = await axiosInstance.get(`${API_BASE_URL}/api/centers/${selectedCenterForSlots.id}/slots`);
        setCenterSlots(res.data?.slots || res.data?.data || []);
      } catch (error) {
        console.error("Failed to refresh slots:", error);
      } finally {
        setSlotsLoading(false);
      }
    }
  };

  // ✅ Open commissions modal
  const handleOpenCommissionsModal = (center) => {
    setSelectedCenterForCommissions(center);
    setCommissionsModalOpen(true);
  };

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) setCurrentPage(page);
  };

  const handleItemsPerPageChange = (value) => {
    setItemsPerPage(Number(value));
    setCurrentPage(1);
  };

  const handleOpenCreateModal = () => {
    setSelectedCenter(null);
    setModalOpen(true);
  };

  const handleOpenEditModal = async (center) => {
    try {
      const res = await axiosInstance.get(`${API_BASE_URL}/api/centers/${center.id}`, { withCredentials: true });
      const data = res.data ? { ...res.data, showApp: Boolean(res.data.showApp), recommended: Boolean(res.data.recommended) } : res.data;
      setSelectedCenter(data);
      setModalOpen(true);
    } catch (e) {
      console.error("Failed to load center details:", e);
      alert("Failed to load center details");
    }
  };

  const handleModalSave = (savedCenter) => {
    const normalized = savedCenter ? { ...savedCenter, showApp: Boolean(savedCenter.showApp), recommended: Boolean(savedCenter.recommended) } : savedCenter;
    if (selectedCenter) {
      setCenters((prev) => prev.map((c) => (c.id === normalized.id ? normalized : c)));
    } else {
      setCenters((prev) => [normalized, ...prev]);
    }
    setModalOpen(false);
    setSelectedCenter(null);
  };

  const handleDeleteCenter = async (id) => {
    if (!window.confirm("Are you sure you want to delete this center?")) return;
    try {
      await axios.delete(`${API_BASE_URL}/api/centers/${id}`, { withCredentials: true });
      setCenters((prev) => prev.filter((c) => c.id !== id));
    } catch (err) {
      console.error("Delete failed:", err);
      alert("Failed to delete center");
    }
  };

  const formatPhoneNumber = (phone) => (!phone ? "-" : `+91 ${phone}`);
  const getCityLabel = (center) => {
    if (!center) return "-";
    if (typeof center.city === "string") return center.city || "-";
    return center.city?.name || "-";
  };
  const getTypeLabel = (center) => (center.isSelf ? "Own" : "Partner");
  const getTypeClass = (center) => center.isSelf ? "bg-green-100 text-green-800 border-green-200" : "bg-blue-100 text-blue-800 border-blue-200";
  const getShowAppClass = (center) => center.showApp ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-gray-50 text-gray-600 border-gray-200";
  const getRecommendedClass = (center) => center.recommended ? "bg-yellow-50 text-yellow-800 border-yellow-200" : "bg-gray-50 text-gray-600 border-gray-200";

  const stats = {
    total: centers.length,
    ownCenters: centers.filter((c) => c.isSelf).length,
    partnerCenters: centers.filter((c) => !c.isSelf).length,
    appVisible: centers.filter((c) => c.showApp).length,
    recommended: centers.filter((c) => c.recommended).length,
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header onMenuToggle={handleMenuToggle} isMenuOpen={sidebarOpen} />
      <Sidebar
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        isCollapsed={sidebarCollapsed}
        onCollapseToggle={() => setSidebarCollapsed(!sidebarCollapsed)}
      />

      <main className={`pt-16 transition-all duration-300 ${sidebarCollapsed ? "lg:ml-16" : "lg:ml-60"}`}>
        <div className="p-3 sm:p-4 lg:p-6">
          {/* Header */}
          <div className="mb-5">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div>
                <h1 className="text-lg sm:text-xl font-semibold text-gray-900">B2B-Centers</h1>
                <p className="text-xs text-gray-500 mt-0.5">Manage all centers and their information</p>
              </div>
              <button
                onClick={handleOpenCreateModal}
                disabled={!canAccess(currentUser, "canCreate")}
                className="inline-flex items-center justify-center px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white text-xs font-medium rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Plus className="w-3.5 h-3.5 mr-1.5" />
                Add Center
              </button>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mt-4">
              {[
                { label: "Total Centers", value: stats.total, icon: Building2, color: "blue" },
                { label: "Own Centers", value: stats.ownCenters, icon: User, color: "green" },
                { label: "Partner Centers", value: stats.partnerCenters, icon: Building2, color: "blue" },
                { label: "Show in App", value: stats.appVisible, icon: Smartphone, color: "emerald" },
                { label: "Recommended", value: stats.recommended, icon: Star, color: "yellow" },
              ].map(({ label, value, icon: Icon, color }) => (
                <div key={label} className="bg-white p-3 rounded border border-gray-200">
                  <div className="flex items-center">
                    <div className={`p-1.5 bg-${color}-50 rounded`}>
                      <Icon className={`w-4 h-4 text-${color}-600`} />
                    </div>
                    <div className="ml-2.5">
                      <p className="text-xs text-gray-500">{label}</p>
                      <p className="text-base font-semibold text-gray-900">{value}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Search */}
          <div className="bg-white rounded border border-gray-200 mb-4">
            <div className="p-3 border-b border-gray-200">
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
                <div className="relative flex-1 md:max-w-md">
                  <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search by name, contact, email, city, address..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-9 pr-3 py-2 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-gray-600">Show:</span>
                  <select
                    value={itemsPerPage}
                    onChange={(e) => handleItemsPerPageChange(e.target.value)}
                    className="text-xs border border-gray-300 rounded px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-blue-500"
                  >
                    {[5, 10, 20, 50].map((n) => <option key={n} value={n}>{n}</option>)}
                  </select>
                  <span className="text-xs text-gray-600">per page</span>
                </div>
              </div>
            </div>

            <div className="p-3">
              {loading ? (
                <div className="flex flex-col items-center justify-center py-10">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mb-2" />
                  <p className="text-xs text-gray-500">Loading centers...</p>
                </div>
              ) : filteredCenters.length === 0 ? (
                <div className="text-center py-10">
                  <div className="inline-flex items-center justify-center w-10 h-10 bg-gray-100 rounded-full mb-3">
                    <Building2 className="w-5 h-5 text-gray-400" />
                  </div>
                  <h3 className="text-xs font-medium text-gray-900 mb-1">No centers found</h3>
                  <p className="text-xs text-gray-500">{searchTerm ? "Try adjusting your search" : "Add your first center"}</p>
                </div>
              ) : (
                <>
                  {/* MOBILE cards */}
                  <div className="grid grid-cols-1 gap-3 sm:hidden">
                    {getCurrentPageItems().map((center, index) => (
                      <div key={center.id} className="bg-white border border-gray-200 rounded p-3">
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              <div className="w-6 h-6 bg-blue-50 rounded flex items-center justify-center flex-shrink-0">
                                <span className="text-xs font-bold text-blue-600">
                                  {(currentPage - 1) * itemsPerPage + index + 1}
                                </span>
                              </div>
                              <p className="text-sm font-semibold text-gray-900 truncate">{center.name || "-"}</p>
                            </div>
                            <div className="flex flex-wrap gap-1.5 mb-2">
                              <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getTypeClass(center)}`}>
                                {getTypeLabel(center)}
                              </span>
                              <button onClick={() => toggleShowApp(center)} className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border ${getShowAppClass(center)}`}>
                                {center.showApp ? <><Eye className="w-3 h-3" /> App</> : <><EyeOff className="w-3 h-3" /> Hidden</>}
                              </button>
                              <button onClick={() => toggleRecommended(center)} className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border ${getRecommendedClass(center)}`}>
                                {center.recommended ? <><Star className="w-3 h-3" /> Rec</> : <><StarOff className="w-3 h-3" /> No</>}
                              </button>
                            </div>
                            <div className="space-y-1">
                              <div className="flex items-start gap-1.5">
                                <MapPin className="w-3.5 h-3.5 text-gray-400 mt-0.5 flex-shrink-0" />
                                <p className="text-xs text-gray-600">{center.address || "No address"} · {getCityLabel(center)}</p>
                              </div>
                              {center.mobile && (
                                <div className="flex items-center gap-1.5">
                                  <Phone className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                                  <span className="text-xs text-gray-700">{formatPhoneNumber(center.mobile)}</span>
                                </div>
                              )}
                            </div>
                          </div>
                          <div className="flex flex-col items-center gap-1">
                            <button onClick={() => handleOpenCommissionsModal(center)} className="p-2 text-gray-500 hover:text-purple-600 hover:bg-purple-50 rounded" title="Commissions">
                              <BadgePercent className="w-4 h-4" />
                            </button>
                            <button onClick={() => handleOpenSlotsModal(center)} className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded" title="Manage Slots">
                              <Clock className="w-4 h-4" />
                            </button>
                            <button onClick={() => canAccess(currentUser, "canEdit") && handleOpenEditModal(center)} disabled={!canAccess(currentUser, "canEdit")} className="p-2 text-gray-500 hover:text-green-600 hover:bg-green-50 rounded disabled:opacity-50 disabled:cursor-not-allowed" title="Edit">
                              <Edit2 className="w-4 h-4" />
                            </button>
                            <button onClick={() => canAccess(currentUser, "canDelete") && handleDeleteCenter(center.id)} disabled={!canAccess(currentUser, "canDelete")} className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded disabled:opacity-50 disabled:cursor-not-allowed" title="Delete">
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* DESKTOP table */}
                  <div className="hidden sm:block overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="bg-gray-50">
                          {["Sl No", "Center Details", "Type", "Recommended", "Show in App", "Contact Info", "City", "Actions"].map((h) => (
                            <th key={h} className="py-2.5 px-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider border-b">
                              {h}
                            </th>
                          ))}
                        </tr>
                      </thead>

                      <tbody className="divide-y divide-gray-200">
                        {getCurrentPageItems().map((center, index) => (
                          <tr key={center.id} className="hover:bg-gray-50 transition-colors">
                            {/* # */}
                            <td className="py-3 px-3 text-center">
                              <span className="text-xs font-medium text-gray-700">
                                {(currentPage - 1) * itemsPerPage + index + 1}
                              </span>
                            </td>

                            {/* Details */}
                            <td className="py-3 px-3">
                              <div className="flex items-start">
                                <div className="flex-shrink-0 w-8 h-8 bg-blue-50 rounded flex items-center justify-center">
                                  <Building2 className="w-4 h-4 text-blue-600" />
                                </div>
                                <div className="ml-2.5">
                                  <h3 className="text-xs font-medium text-gray-900 truncate max-w-[220px]">{center.name || "-"}</h3>
                                  <div className="flex items-center mt-0.5">
                                    <MapPin className="w-2.5 h-2.5 text-gray-400 mr-1 flex-shrink-0" />
                                    <p className="text-xs text-gray-500 truncate max-w-[200px]">{center.address || "No address"}</p>
                                  </div>
                                </div>
                              </div>
                            </td>

                            {/* Type */}
                            <td className="py-3 px-3">
                              <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${getTypeClass(center)}`}>
                                {getTypeLabel(center)}
                              </span>
                            </td>

                            {/* Recommended */}
                            <td className="py-3 px-3">
                              <button
                                onClick={() => toggleRecommended(center)}
                                className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium border ${getRecommendedClass(center)}`}
                              >
                                {center.recommended ? <><Star className="w-3.5 h-3.5" /> Yes</> : <><StarOff className="w-3.5 h-3.5" /> No</>}
                              </button>
                            </td>

                            {/* Show in App */}
                            <td className="py-3 px-3">
                              <button
                                onClick={() => toggleShowApp(center)}
                                className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium border ${getShowAppClass(center)}`}
                              >
                                {center.showApp ? <><Eye className="w-3.5 h-3.5" /> Visible</> : <><EyeOff className="w-3.5 h-3.5" /> Hidden</>}
                              </button>
                            </td>

                            {/* Contact */}
                            <td className="py-3 px-3">
                              <div className="space-y-1">
                                {center.contactName && (
                                  <div className="flex items-center">
                                    <User className="w-2.5 h-2.5 text-gray-400 mr-1.5 flex-shrink-0" />
                                    <span className="text-xs text-gray-700 truncate max-w-[150px]">{center.contactName}</span>
                                  </div>
                                )}
                                {center.email && (
                                  <div className="flex items-center">
                                    <Mail className="w-2.5 h-2.5 text-gray-400 mr-1.5 flex-shrink-0" />
                                    <a href={`mailto:${center.email}`} className="text-xs text-blue-600 hover:underline truncate max-w-[170px]">{center.email}</a>
                                  </div>
                                )}
                                {center.mobile && (
                                  <div className="flex items-center">
                                    <Phone className="w-2.5 h-2.5 text-gray-400 mr-1.5 flex-shrink-0" />
                                    <a href={`tel:+91${center.mobile}`} className="text-xs text-gray-700 hover:text-blue-600">{formatPhoneNumber(center.mobile)}</a>
                                  </div>
                                )}
                              </div>
                            </td>

                            {/* City */}
                            <td className="py-3 px-3">
                              <p className="text-xs text-gray-700">{getCityLabel(center)}</p>
                            </td>

                            {/* Actions */}
                            <td className="py-3 px-3">
                              <div className="flex items-center space-x-1">
                                {/* ✅ Commission button */}
                                <button
                                  onClick={() => handleOpenCommissionsModal(center)}
                                  className="p-1 text-gray-400 hover:text-purple-600 hover:bg-purple-50 rounded transition"
                                  title="Manage Commissions"
                                >
                                  <BadgePercent className="w-3.5 h-3.5" />
                                </button>

                                <button
                                  onClick={() => handleOpenSlotsModal(center)}
                                  className="p-1 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded transition"
                                  title="Manage Slots"
                                >
                                  <Clock className="w-3.5 h-3.5" />
                                </button>

                                <button
                                  onClick={() => canAccess(currentUser, "canEdit") && handleOpenEditModal(center)}
                                  disabled={!canAccess(currentUser, "canEdit")}
                                  className="p-1 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded transition disabled:opacity-50 disabled:cursor-not-allowed"
                                  title="Edit Center"
                                >
                                  <Edit2 className="w-3.5 h-3.5" />
                                </button>

                                <button
                                  onClick={() => canAccess(currentUser, "canDelete") && handleDeleteCenter(center.id)}
                                  disabled={!canAccess(currentUser, "canDelete")}
                                  className="p-1 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition disabled:opacity-50 disabled:cursor-not-allowed"
                                  title="Delete Center"
                                >
                                  <Trash2 className="w-3.5 h-3.5" />
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}

              {/* Pagination */}
              {filteredCenters.length > 0 && !loading && (
                <div className="mt-4 flex flex-col sm:flex-row items-center justify-between gap-3 pt-3 border-t border-gray-200">
                  <div className="text-xs text-gray-500">
                    Showing{" "}
                    <span className="font-medium">{Math.min((currentPage - 1) * itemsPerPage + 1, filteredCenters.length)}</span>
                    {" "}to{" "}
                    <span className="font-medium">{Math.min(currentPage * itemsPerPage, filteredCenters.length)}</span>
                    {" "}of{" "}
                    <span className="font-medium">{filteredCenters.length}</span> centers
                  </div>
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                      className="p-1.5 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </button>
                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                      let pageNum;
                      if (totalPages <= 5) pageNum = i + 1;
                      else if (currentPage <= 3) pageNum = i + 1;
                      else if (currentPage >= totalPages - 2) pageNum = totalPages - 4 + i;
                      else pageNum = currentPage - 2 + i;
                      return (
                        <button
                          key={pageNum}
                          onClick={() => handlePageChange(pageNum)}
                          className={`w-8 h-8 text-xs font-medium rounded ${currentPage === pageNum ? "bg-blue-600 text-white" : "text-gray-700 hover:bg-gray-100"}`}
                        >
                          {pageNum}
                        </button>
                      );
                    })}
                    <button
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === totalPages}
                      className="p-1.5 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <ChevronRight className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Modals */}
        <ReferenceCenterModal
          isOpen={modalOpen}
          onClose={() => { setModalOpen(false); setSelectedCenter(null); }}
          onSave={handleModalSave}
          center={selectedCenter}
          mode={selectedCenter ? "edit" : "create"}
        />

        <SlotsManagementModal
          isOpen={slotsModalOpen}
          onClose={() => { setSlotsModalOpen(false); setSelectedCenterForSlots(null); setCenterSlots([]); }}
          center={selectedCenterForSlots}
          slots={centerSlots}
          loading={slotsLoading}
          onSlotsUpdated={handleSlotsUpdated}
          categories={centerCategories}
        />

        {/* ✅ Commissions modal */}
        <CenterCommissionsModal
          isOpen={commissionsModalOpen}
          onClose={() => { setCommissionsModalOpen(false); setSelectedCenterForCommissions(null); }}
          center={selectedCenterForCommissions}
        />
      </main>
    </div>
  );
}

export default Centers;